import React, { type SyntheticEvent, useState } from 'react'
import { Dialog } from '../../common/Dialog'
import { authenticatedFetchData, API_HOST } from '../../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import { DateWithUTCTooltip } from '../../common'
import { UserSearch } from '../../common/UserSearch'
import { Button } from '@tomra/design-system-react/button'

type Props = {
  closeDialog: () => void
  bagToLink: UnlinkedBagType
  bagLinkedSuccessFully: () => void
}

export const LinkUnclaimedBagDialog = ({ closeDialog, bagToLink, bagLinkedSuccessFully }: Props) => {
  const [user, setUser] = useState<UserInfoType>()
  const [linkUnclaimedBagStatus, setLinkUnclaimedBagStatus] = useState('')

  const linkUnclaimedBag = (e: SyntheticEvent) => {
    e.preventDefault()

    setLinkUnclaimedBagStatus('loading')

    const options = {
      method: 'POST',
      body: JSON.stringify({
        bagtag: bagToLink.bagTag,
        userId: user?.id,
        created: new Date().toISOString()
      })
    }

    authenticatedFetchData(API_HOST + '/mytomra/v1.0/hotlinetool/unclaimed', options)
      .run()
      .then(
        () => {
          setLinkUnclaimedBagStatus('success')

          window.setTimeout(() => {
            setLinkUnclaimedBagStatus('')
            bagLinkedSuccessFully()
          }, 2000)
        },
        (error: Error) => {
          setLinkUnclaimedBagStatus('failed')
          logError(new Error('Failed to link bag to user'), error)

          window.setTimeout(() => {
            setLinkUnclaimedBagStatus('')
          }, 2000)
        }
      )
  }

  return (
    <Dialog ariaLabel="Link bag to user" onClose={closeDialog} position="TOP">
      {linkUnclaimedBagStatus === 'loading' ? (
        <div className="loadingSpinner" />
      ) : linkUnclaimedBagStatus === 'failed' ? (
        <div className="rounded-lg border-l-[3px] border-rose-500 bg-white p-4 text-left shadow-md dark:bg-gray-900">
          Whoopsie! Failed to link bag to user
        </div>
      ) : linkUnclaimedBagStatus === 'success' ? (
        <div className="rounded-lg border-l-[3px] border-emerald-500 bg-white p-4 text-left shadow-md dark:bg-gray-900">
          Bag linked successfully!
        </div>
      ) : (
        <div className="rounded-2xl bg-white shadow-md dark:bg-gray-900 p-6 w-[80vw] mt-[5vh]">
          <h1 className="text-xl mb-4">Link bag to user</h1>

          <dl className="mb-4">
            <dt id="bagtag" className="mb-2 text-gray-600">
              Bagtag
            </dt>
            <dd aria-labelledby="bagtag">{bagToLink.bagTag}</dd>
          </dl>

          <dl className="mb-4">
            <dt id="date" className="mb-2 text-gray-600">
              Date
            </dt>
            <dd aria-labelledby="date">
              <DateWithUTCTooltip date={new Date(bagToLink.processedAt)} />
            </dd>
          </dl>

          <dl className="mb-4">
            <dt id="serial" className="mb-2 text-gray-600">
              Serial
            </dt>
            <dd aria-labelledby="serial">{bagToLink.serialNumber}</dd>
          </dl>

          <dl className="mb-4">
            <dt id="amount" className="mb-2 text-gray-600">
              Amount
            </dt>
            <dd aria-labelledby="amount">{bagToLink.amount.toFixed(2)} AUD</dd>
          </dl>

          <dl className="mb-4">
            <dt id="location" className="mb-2 text-gray-600">
              Store Reference
            </dt>
            <dd aria-labelledby="location">{bagToLink.storeReference}</dd>
          </dl>

          {!user && (
            <div className="w-1/2">
              <UserSearch selectUser={user => setUser(user)} />
            </div>
          )}

          {user && (
            <>
              <div className="mt-4 flex items-center space-x-4">
                <h2 className="text-lg">Selected user</h2>

                <Button onClick={() => setUser(undefined)}>Change user</Button>
              </div>

              <dl className="mb-4">
                <dt id="userId" className="mb-2 text-gray-600">
                  ID
                </dt>
                <dd aria-labelledby="userId">{user.id}</dd>
              </dl>

              <dl className="mb-4">
                <dt id="name">Name</dt>
                <dd aria-labelledby="name">{user.firstName}</dd>
              </dl>

              <dl className="mb-4">
                <dt id="email">Email</dt>
                <dd aria-labelledby="email">{user.email}</dd>
              </dl>

              <div className="flex space-x-4 justify-end mt-6">
                <Button onClick={() => closeDialog()}>Cancel</Button>

                <Button variant="primary" onClick={linkUnclaimedBag}>
                  {`Link bag to ${user.firstName}`}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Dialog>
  )
}
